import { useEffect, useState } from 'react';
import { Stack, Flex, Button, Group, Text, ScrollArea } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';

import {
  useBusinessInvitations,
  useNotification,
  useTracking,
} from '../../hooks';
import { useTranslation } from 'react-i18next';
import classes from './InviteSuppliers.module.css';
import { z } from 'zod';
import { InvitationPayload } from '../../shared/types';
import { TFunction } from 'i18next';
import { BetaIcon } from '../../../lib/components/BetaIcon/BetaIcon';
import Papa from 'papaparse';

type ParsedFile = {
  filename: string;
  fileSize: number;
  data?: { email: string }[];
};

type BulkInviteSupplierProps = {
  cancel: () => void;
};

const validateSuppliers = (
  setInvitationsResponse: (response: {
    errors: Record<string, string[]>;
    invitations: InvitationPayload[];
  }) => void,
  file: FileWithPath,
  t: TFunction<string>
): Promise<ParsedFile> => {
  const reader = new FileReader();
  reader.readAsText(file as FileWithPath);

  return new Promise((resolve, reject) => {
    reader.onload = (event: any) => {
      try {
        Papa.parse(event.target.result, {
          complete: (results: any) => {
            const data = results.data
              .filter((line: string[]) => line[0]?.trim() !== '')
              .filter((line: string[]) => {
                const emailSchema = z.string().email();
                try {
                  emailSchema.parse(line[0]);
                  return true;
                } catch {
                  return false;
                }
              })
              .map((line: string[]) => ({
                email: line[0],
              }));
            if (data.length >= 1000) {
              setInvitationsResponse({
                errors: {
                  [t('invite.bulk.tooManyEmails')]: [file.name],
                },
                invitations: [],
              });
              reject({ e: 'Too many emails', file });
            }
            resolve({
              filename: file.name,
              fileSize: file.size,
              data: data,
            });
          },
        });
      } catch (e) {
        reject({ e, file });
      }
    };
  });
};

export const BulkUploadCsv = ({ cancel }: BulkInviteSupplierProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { batchInvitationMutation } = useBusinessInvitations(undefined);
  const { showSuccess } = useNotification();
  const [parsedFiles, setParsedFiles] = useState<ParsedFile[]>([]);
  const [invitationsResponse, setInvitationsResponse] = useState<{
    errors: Record<string, string[]>;
    invitations: InvitationPayload[];
  }>({
    errors: {},
    invitations: [],
  });

  useEffect(() => {
    trackEvent('Supplier', {
      step: 'supplier-invite',
      action: 'open',
    });
  }, [trackEvent]);

  const parseUploadedFiles = (filesToParse: FileWithPath[]) => {
    Promise.all(
      filesToParse.map((file: FileWithPath) =>
        validateSuppliers(setInvitationsResponse, file, t)
      )
    ).then((parsedFiles) => {
      parsedFiles.forEach((parsedFile: any) => {
        // At this point we have a validated CSV file contents
        setParsedFiles((parsedFiles: ParsedFile[]) => [
          ...parsedFiles,
          parsedFile as ParsedFile,
        ]);
      });
    });
  };

  return (
    <>
      <Stack>
        <Flex justify="space-between" align="center" direction="column">
          <Dropzone
            // playwright isn't finding data-testid, using id instead
            // do not change or e2e tests will fail
            id="dropzone"
            className={classes.root}
            style={{ width: '100%' }}
            accept={{
              'application/csv': ['.csv'],
            }}
            maxSize={2 * 1024 ** 2}
            onDrop={(files: FileWithPath[]) => {
              parseUploadedFiles(files);
            }}
            // onReject={(rejects) =>
            //     rejects.forEach((reject) =>

            //     )
            // }
            onFileDialogCancel={() =>
              trackEvent('Supplier', {
                step: 'bulk-invite',
                action: 'upload-cancel',
              })
            }
          >
            <Group mih={100} align="center" m={0}>
              <Flex
                justify="center"
                align="center"
                direction="row"
                gap="md"
                w="100%"
                style={{
                  border: '1px dashed #6B666F',
                  padding: '16px',
                  cursor: 'pointer',
                }}
              >
                <BetaIcon icon="upload" />
                <Text size="md" inline>
                  {t('invite.bulk.uploadButtonText')}
                </Text>
              </Flex>
            </Group>
          </Dropzone>
          {invitationsResponse.errors &&
            !!Object.keys(invitationsResponse.errors).length &&
            Object.keys(invitationsResponse.errors).map((error, index) => (
              <ScrollArea
                key={index}
                w="100%"
                h={100}
                p="xs"
                scrollbarSize={10}
                scrollHideDelay={0}
                scrollbars="y"
                styles={{
                  scrollbar: {
                    backgroundColor: 'var(--mantine-color-dark-3)',
                  },
                  thumb: {
                    backgroundColor: 'var(--mantine-color-dark-6)',
                  },
                }}
              >
                <Text size="sm" c="red" style={{ width: '100%' }} key={index}>
                  {error}:
                </Text>
                {!!invitationsResponse.errors[error] &&
                  invitationsResponse.errors[error]?.map(
                    (line: string, i: number) => (
                      <Text size="sm" c="red" style={{ width: '100%' }} key={i}>
                        {line}
                      </Text>
                    )
                  )}
              </ScrollArea>
            ))}
          {!!parsedFiles.length &&
            parsedFiles.map((file, index) => {
              const { data, filename, fileSize } = file;
              const fileSizeInMB = (fileSize / 1024 ** 2).toFixed(2);
              return (
                <Text size="sm" style={{ width: '100%' }} key={index}>
                  {`${filename} - ${fileSizeInMB} MB - ${data?.length} emails`}
                </Text>
              );
            })}
        </Flex>
      </Stack>
      <Flex justify="end" gap="sm" mt="lg">
        <Button
          variant="outline"
          size="sm"
          onClick={cancel}
          className={classes.button}
        >
          {t('common.cancel')}
        </Button>
        <Button
          size="sm"
          type="submit"
          variant="outline"
          disabled={
            !parsedFiles.length &&
            !parsedFiles.some((file) => !file.data?.length)
          }
          onClick={async () => {
            const payload = await batchInvitationMutation.mutateAsync(
              parsedFiles
                .flatMap((file) => file.data || [])
                .filter((email, index, self) => {
                  return (
                    self.findIndex((e) => e.email === email.email) === index
                  );
                })
            );
            setInvitationsResponse(payload);
            if (!Object.keys(payload.errors).length) {
              cancel();
              showSuccess(
                t('invite.bulk.uploadSuccess', {
                  count: payload.invitations.length,
                })
              );
            }
          }}
        >
          {t('common.upload')}
        </Button>
      </Flex>
    </>
  );
};
